import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantService } from '../services/tenants';
import { ITenant } from '@order-up/models';
import { ILocation } from '@order-up/models';
import { type CreateTenantType } from '@order-up/schemas';

/**
 * Defines the query keys used for tenant-related data.
 */
export const tenantKeys = {
    all: ['tenants'] as const,
    lists: () => [...tenantKeys.all, 'list'] as const,
    listByUser: (userId: string) => [...tenantKeys.lists(), { userId }] as const,
    listAll: (limit?: number) => [...tenantKeys.lists(), { limit }] as const,
    details: () => [...tenantKeys.all, 'detail'] as const,
    detailById: (id: number) => [...tenantKeys.details(), id] as const,
    detailByGuid: (guid: string) => [...tenantKeys.details(), guid] as const,
};

/**
 * A hook to fetch tenants associated with a specific user ID.
 * @param userId The UID of the user.
 */
export const useGetTenantsByUserId = (userId: string) => {
    return useQuery({
        queryKey: tenantKeys.listByUser(userId),
        queryFn: () => tenantService.getTenantsByUserId(userId),
        enabled: !!userId,
    });
};

/**
 * A hook to fetch a single tenant by its numeric tenant ID.
 * @param tenantId The numeric ID of the tenant.
 */
export const useGetTenantById = (tenantId: number) => {
    return useQuery({
        queryKey: tenantKeys.detailById(tenantId),
        queryFn: () => tenantService.getTenantById(tenantId),
        enabled: !!tenantId,
    });
};

/**
 * A hook to fetch a single tenant by its unique GUID.
 * @param guid The GUID of the tenant.
 */
export const useGetTenantByGuid = (guid: string) => {
    return useQuery({
        queryKey: tenantKeys.detailByGuid(guid),
        queryFn: () => tenantService.getTenantByGuid(guid),
        enabled: !!guid,
    });
};

/**
 * A hook to create a new tenant.
 * @returns A mutation object from TanStack Query.
 */
export const useCreateTenant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (tenantData: CreateTenantType) => tenantService.createTenant(tenantData),
        onSuccess: () => {
            // Invalidate all tenant lists to refetch and include the new tenant.
            queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
        },
    });
};

/**
 * A hook to update an existing tenant.
 * @returns A mutation object from TanStack Query.
 */
export const useUpdateTenant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ guid, data }: { guid: string; data: Partial<ITenant> }) => tenantService.updateTenant(guid, data),
        onSuccess: (_, variables) => {
            // Invalidate the specific tenant's detail query and all tenant lists.
            queryClient.invalidateQueries({ queryKey: tenantKeys.detailByGuid(variables.guid) });
            queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
        },
    });
};


/**
 * A hook to fetch all tenants in the system (for admin operations).
 * @param limit Optional limit on the number of tenants to fetch.
 */
export const useGetAllTenants = (limit?: number) => {
    return useQuery({
        queryKey: tenantKeys.listAll(limit),
        queryFn: () => tenantService.getAllTenants(limit),
    });
}; 